import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getApiClient, getSessionCookieHeader } from "~/utils/api-client";
import { parseApiError } from "~/utils/api-error";

export const PIN_KEYS = {
	all: ["pin"] as const,
	session: () => [...PIN_KEYS.all, "session"] as const,
};

const PinSessionSchema = z.object({
	invalidatePinIfStale: z.boolean().optional(),
});

const PinResetTokenVerifySchema = z.object({
	token: z.string().min(1),
});

// Default grace period used when session is invalid (e.g., 401)
// This should match the API's PIN_BACKGROUND_GRACE_PERIOD_MS value
const DEFAULT_PIN_GRACE_PERIOD_MS = 2 * 60 * 1000;

type PinSessionResponse = {
	pinRequired: boolean;
	pinEnabled: boolean;
	sessionValid: boolean;
	pinGracePeriodMs: number;
};

export const checkPinSession = createServerFn({ method: "POST" })
	.validator(PinSessionSchema)
	.handler(async ({ data }) => {
		const api_client = getApiClient(getSessionCookieHeader());

		const result = await api_client.self.api.session.check.$post({
			query: data.invalidatePinIfStale
				? { invalidatePinIfStale: "true" }
				: undefined,
		});

		if (result.status === 401) {
			return {
				pinRequired: false,
				pinEnabled: false,
				sessionValid: false,
				pinGracePeriodMs: DEFAULT_PIN_GRACE_PERIOD_MS,
			} satisfies PinSessionResponse;
		}

		if (!result.ok) {
			await parseApiError(result, "Failed to check PIN session");
		}

		return await result.json();
	});

export const pinSessionQueryOptions = queryOptions({
	queryKey: PIN_KEYS.session(),
	queryFn: () => checkPinSession({ data: { invalidatePinIfStale: false } }),
});

type PinResetTokenVerifyResponse = {
	valid: boolean;
};

export const verifyPinResetToken = createServerFn({ method: "POST" })
	.validator(PinResetTokenVerifySchema)
	.handler(async ({ data }) => {
		const api_client = getApiClient(getSessionCookieHeader());

		const result = await api_client.self.api.pin.reset.verify.$post({
			json: { token: data.token },
		});

		if (!result.ok) {
			return { valid: false } satisfies PinResetTokenVerifyResponse;
		}

		return await result.json();
	});
